import {
  Alert,
  AlertTitle,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, type ChangeEvent } from "react";
import { type CrownEnv } from "../../../components/crown-data-import/env/env-config";
import { PRODUCTION_CONFIRMATION_PHRASE } from "../../outbound-test-bench/bench-run";

const styles = {
  target: {
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase",
  } satisfies SxProps<Theme>,
  confirmation: { mt: 1, maxWidth: 320 } satisfies SxProps<Theme>,
};

type EnvironmentBannerProps = {
  readonly env: CrownEnv;
  /** The backend's own APP_ENV, once the connection has loaded. */
  readonly serverEnvironment: string | null;
  readonly production: boolean;
  readonly confirmation: string;
  readonly onConfirmationChange: (value: string) => void;
};

/**
 * Names the environment every save on this page writes to, in large type.
 * In production it also holds the typed confirmation that unlocks saving.
 */
const EnvironmentBanner = ({
  env,
  serverEnvironment,
  production,
  confirmation,
  onConfirmationChange,
}: EnvironmentBannerProps) => {
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onConfirmationChange(event.target.value);
    },
    [onConfirmationChange],
  );

  const target = (
    <Typography component="span" sx={styles.target} variant="h5">
      {env}
      {serverEnvironment === null ? "" : ` (backend: ${serverEnvironment})`}
    </Typography>
  );

  if (!production) {
    return (
      <Alert severity="info">
        <AlertTitle>Saves on this page write to {target}</AlertTitle>
        Switch environments from the launcher sidebar.
      </Alert>
    );
  }

  return (
    <Alert severity="error">
      <AlertTitle>Saves on this page write to {target}</AlertTitle>
      These are live carrier and customer settings. Type{" "}
      <strong>{PRODUCTION_CONFIRMATION_PHRASE}</strong> to allow saving.
      <TextField
        fullWidth
        label={`Type ${PRODUCTION_CONFIRMATION_PHRASE} to confirm`}
        size="small"
        sx={styles.confirmation}
        value={confirmation}
        onChange={handleChange}
      />
    </Alert>
  );
};

export default EnvironmentBanner;
